/**
 * Per-user arrangement of the existing Home sections.
 *
 * The header (greeting and Start Work) stays fixed. Every section that already
 * renders below it can be moved, hidden, restored, or reset. Nothing here
 * reorders or hides a section because of its data. Permission uses the same
 * gates the sections already use, so an unauthorized section is not on the board.
 */

export const HOME_WIDGETS = Object.freeze({
  needs: {
    id: "needs",
    label: "Needs Attention",
    permission: "attention",
  },
  today: {
    id: "today",
    label: "Today at a Glance",
    permission: null,
  },
  health: {
    id: "health",
    label: "Project Health",
    permission: "tasks",
  },
  taskov: {
    id: "taskov",
    label: "Task Overview",
    permission: "tasks",
  },
  planning: {
    id: "planning",
    label: "Planning – Next 7 Days",
    permission: "tasks",
  },
  cash: {
    id: "cash",
    label: "Revenue & Cash Flow",
    permission: "finance",
  },
  labour: {
    id: "labour",
    label: "Labour Hours",
    permission: "timeLogs",
  },
  pipeline: {
    id: "pipeline",
    label: "Estimate Pipeline",
    permission: "estimates",
  },
  finance: {
    id: "finance",
    label: "Financial Position",
    permission: "financeSummary",
  },
  activity: {
    id: "activity",
    label: "Recent Activity",
    permission: null,
  },
  quick: {
    id: "quick",
    label: "Quick Access",
    permission: "quick",
  },
});

/** The section order the board already used, before any user arrangement. */
export const DEFAULT_HOME_ORDER = Object.freeze({
  mobile: Object.freeze([
    "needs",
    "today",
    "health",
    "taskov",
    "planning",
    "cash",
    "labour",
    "pipeline",
    "finance",
    "activity",
  ]),
  desktop: Object.freeze([
    "needs",
    "today",
    "cash",
    "health",
    "taskov",
    "labour",
    "pipeline",
    "planning",
    "finance",
    "activity",
    "quick",
  ]),
});

export function widgetPermitted(id, allow = {}) {
  const widget = HOME_WIDGETS[id];
  if (!widget) return false;
  if (widget.permission === "attention") {
    return Boolean(allow.tasks || allow.receivables || allow.estimates);
  }
  if (widget.permission === "financeSummary") {
    return Boolean(allow.finance || allow.receivables || allow.payables);
  }
  if (widget.permission === "quick") {
    return Boolean(allow.quick);
  }
  if (!widget.permission) return true;
  return Boolean(allow[widget.permission]);
}

function baselineFor(variant) {
  return DEFAULT_HOME_ORDER[variant] || DEFAULT_HOME_ORDER.mobile;
}

/**
 * @returns {{ visible: string[], hidden: string[], customized: boolean }}
 */
export function resolveHomeDashboardLayout({
  variant = "mobile",
  allow = {},
  preference = null,
} = {}) {
  const baseline = baselineFor(variant).filter((id) => widgetPermitted(id, allow));
  const customized = Boolean(preference?.customized);

  if (!customized) {
    return { visible: baseline, hidden: [], customized: false };
  }

  const savedOrder = Array.isArray(preference.order) ? preference.order : [];
  const savedHidden = new Set(
    Array.isArray(preference.hidden) ? preference.hidden : [],
  );
  const placed = new Set();
  const visible = [];
  const hidden = [];

  const place = (id) => {
    if (!HOME_WIDGETS[id] || placed.has(id) || !widgetPermitted(id, allow)) return;
    placed.add(id);
    if (savedHidden.has(id)) hidden.push(id);
    else visible.push(id);
  };

  savedOrder.forEach(place);
  savedHidden.forEach(place);

  baseline.forEach((id) => {
    if (!placed.has(id)) visible.push(id);
  });

  return { visible, hidden, customized: true };
}

export function moveHomeWidget(layout, id, direction) {
  const visible = [...(layout?.visible || [])];
  const index = visible.indexOf(id);
  if (index < 0) return layout;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= visible.length) return layout;
  return placeHomeWidget(layout, id, target);
}

/**
 * Index in the list after `draggedId` is removed.
 * `boxes` are in current visual order: `{ id, top, height }`.
 */
export function insertionIndex(pointerY, boxes, draggedId) {
  const others = (Array.isArray(boxes) ? boxes : []).filter(
    (box) => box && box.id !== draggedId,
  );
  for (let index = 0; index < others.length; index += 1) {
    const box = others[index];
    const mid = Number(box.top) + Number(box.height) / 2;
    if (pointerY < mid) return index;
  }
  return others.length;
}

export function previewHomeOrder(visible, id, toIndex) {
  const list = [...(visible || [])];
  if (!list.includes(id) || !Number.isInteger(toIndex)) return list;
  const next = list.filter((item) => item !== id);
  const at = Math.max(0, Math.min(toIndex, next.length));
  next.splice(at, 0, id);
  return next;
}

/** Drop a whole section at `toIndex` (index after the section is removed). */
export function placeHomeWidget(layout, id, toIndex) {
  const current = layout?.visible || [];
  if (!current.includes(id)) return layout;
  const visible = previewHomeOrder(current, id, toIndex);
  if (visible.every((item, index) => item === current[index])) return layout;
  return {
    ...layout,
    visible,
    hidden: [...(layout.hidden || [])],
    customized: true,
  };
}

export function hideHomeWidget(layout, id) {
  if (!layout?.visible?.includes(id)) return layout;
  return {
    ...layout,
    visible: layout.visible.filter((item) => item !== id),
    hidden: [...(layout.hidden || []), id],
    customized: true,
  };
}

export function showHomeWidget(layout, id) {
  if (!layout?.hidden?.includes(id)) return layout;
  return {
    ...layout,
    hidden: layout.hidden.filter((item) => item !== id),
    visible: [...(layout.visible || []), id],
    customized: true,
  };
}

/** Home swipe stays on unless Arrange mode is open. */
export function isHomeSwipeEnabled({ arranging = false } = {}) {
  return !arranging;
}
